import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { 
  Globe, 
  Smartphone, 
  Users, 
  BarChart3, 
  Zap, 
  ArrowRight,
  CheckCircle2,
  Settings,
  Share2
} from 'lucide-react';

const services = [
  {
    icon: Globe,
    title: 'Business Website Development',
    description: 'Custom, responsive websites tailored for your business needs. We create modern, fast-loading websites that convert visitors into customers.',
    features: [
      'Mobile-responsive design',
      'SEO-optimized structure',
      'Fast loading performance',
      'Easy content management',
      'Contact forms & integrations',
    ],
    color: 'orange',
  },
  {
    icon: Share2,
    title: 'Digital Presence Setup',
    description: 'Establish and manage your brand across all digital platforms. From Google Business to social media, we help you get discovered.',
    features: [
      'Google Business Profile setup',
      'Social media profile creation',
      'Online directory listings',
      'Brand consistency across platforms',
      'Review management strategy',
    ],
    color: 'blue',
  },
  {
    icon: Users,
    title: 'Causaler Hire - Pool Worker Platform',
    description: 'Our flagship product connecting businesses with flexible, reliable workforce on-demand. Perfect for seasonal peaks and daily operations.',
    features: [
      'Access to verified workers',
      'Flexible hiring options',
      'Simple scheduling system',
      'Integrated payment processing',
      'Performance tracking',
    ],
    color: 'orange',
    featured: true,
  },
  {
    icon: BarChart3,
    title: 'Business Dashboards',
    description: 'Data-driven insights to make smarter business decisions. Track your performance, understand customers and identify growth opportunities.',
    features: [
      'Real-time analytics',
      'Customer insights',
      'Revenue tracking',
      'Performance metrics',
      'Custom reports',
    ],
    color: 'blue',
  },
  {
    icon: Zap,
    title: 'Automation Tools',
    description: 'Streamline repetitive tasks and focus on what matters. From appointment reminders to invoice generation, we automate your workflows.',
    features: [
      'Appointment scheduling',
      'Automated reminders',
      'Invoice generation',
      'Email marketing automation',
      'Inventory alerts',
    ],
    color: 'orange',
  },
  {
    icon: Settings,
    title: 'Custom Solutions',
    description: 'Have unique requirements? We build tailored solutions to address your specific business challenges and opportunities.',
    features: [
      'Custom software development',
      'Third-party integrations',
      'Workflow optimization',
      'Dedicated support',
      'Scalable architecture',
    ],
    color: 'blue',
  },
];

export default function ServicesPage() {
  return (
    <div className="pt-20" data-testid="services-page">
      {/* Hero Section */}
      <section className="py-20 md:py-32 bg-gradient-subtle" data-testid="services-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center">
            <span className="inline-block px-4 py-2 bg-orange-100 text-[#F5941E] rounded-full text-sm font-semibold mb-6">
              Our Services
            </span>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-[#0F172A] leading-tight mb-6">
              Digital Solutions for{' '}
              <span className="text-gradient">Every Need</span>
            </h1>
            <p className="text-lg md:text-xl text-slate-600 leading-relaxed">
              From websites to workforce management, we provide comprehensive tools 
              to help your business succeed in the digital world.
            </p>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-20 md:py-32" data-testid="services-grid">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <Card 
                key={service.title}
                className={`relative overflow-hidden bg-white rounded-3xl border transition-all duration-300 hover:-translate-y-1 ${
                  service.featured 
                    ? 'border-[#F5941E] shadow-lg shadow-orange-500/10' 
                    : 'border-slate-100 shadow-card hover:shadow-card-hover'
                }`}
                data-testid={`service-card-${index}`}
              >
                {service.featured && (
                  <div className="absolute top-0 right-0 bg-[#F5941E] text-white text-xs font-semibold px-4 py-1 rounded-bl-xl">
                    Launching Soon
                  </div>
                )}
                <CardHeader className="p-8 pb-4">
                  <div className={`w-14 h-14 rounded-2xl flex items-center justify-center mb-4 ${
                    service.color === 'orange' ? 'bg-orange-100' : 'bg-blue-100'
                  }`}>
                    <service.icon className={`w-7 h-7 ${
                      service.color === 'orange' ? 'text-[#F5941E]' : 'text-[#1E5E9F]'
                    }`} />
                  </div>
                  <CardTitle className="font-heading text-xl font-bold mb-2">
                    {service.title}
                  </CardTitle>
                  <CardDescription className="text-slate-600 text-base">
                    {service.description}
                  </CardDescription>
                </CardHeader>
                <CardContent className="p-8 pt-4">
                  <ul className="space-y-3 mb-6">
                    {service.features.map((feature, featureIndex) => (
                      <li key={featureIndex} className="flex items-center gap-3">
                        <CheckCircle2 className={`w-5 h-5 flex-shrink-0 ${
                          service.color === 'orange' ? 'text-[#F5941E]' : 'text-[#1E5E9F]'
                        }`} />
                        <span className="text-slate-700 text-sm">{feature}</span>
                      </li>
                    ))}
                  </ul>
                  <Link to="/contact">
                    <Button 
                      variant={service.featured ? 'default' : 'outline'}
                      className={`w-full rounded-full font-semibold transition-all duration-300 ${
                        service.featured 
                          ? 'bg-[#F5941E] hover:bg-[#D97F12] text-white' 
                          : 'border-slate-200 hover:border-[#1E5E9F] text-[#1E5E9F]'
                      }`}
                      data-testid={`service-cta-${index}`}
                    >
                      {service.featured ? 'Get Started' : 'Learn More'}
                      <ArrowRight className="ml-2 w-4 h-4" />
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* How We Work */}
      <section className="py-20 md:py-32 bg-[#F8FAFC]" data-testid="how-we-work">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-[#F5941E] font-semibold text-sm tracking-wider uppercase">
              Our Process
            </span>
            <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold mt-4 mb-6">
              How We Work With You
            </h2>
            <p className="text-slate-600 text-lg max-w-2xl mx-auto">
              A simple, transparent process designed to deliver results quickly
            </p>
          </div>

          <div className="grid md:grid-cols-4 gap-8">
            {[
              { step: '01', title: 'Discover', description: 'We learn about your business, goals, and challenges.' },
              { step: '02', title: 'Plan', description: 'We create a tailored strategy and solution roadmap.' },
              { step: '03', title: 'Build', description: 'We develop and implement your digital solutions.' },
              { step: '04', title: 'Grow', description: 'We support and optimize for continuous growth.' },
            ].map((item, index) => (
              <div 
                key={item.step}
                className="text-center"
                data-testid={`process-step-${index}`}
              >
                <span className="inline-block text-[#F5941E] font-heading font-bold text-5xl mb-4 opacity-30">
                  {item.step}
                </span>
                <h3 className="font-heading text-xl font-bold mb-3">{item.title}</h3>
                <p className="text-slate-600">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 md:py-32 bg-gradient-brand" data-testid="services-cta">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-6">
            Not Sure Where to Start?
          </h2>
          <p className="text-white/90 text-lg md:text-xl mb-10 max-w-2xl mx-auto">
            Let&apos;s have a free consultation to understand your needs and recommend the best solutions for your business.
          </p>
          <Link to="/contact#contact-form">
            <Button 
              className="bg-white text-[#F5941E] hover:bg-slate-100 rounded-full px-8 py-6 text-lg font-semibold transition-all duration-300 hover:scale-105"
              data-testid="services-cta-button"
            >
              Schedule Free Consultation
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
}
